import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type {
  PriceListFilters,
  CreatePriceListData,
  UpdatePriceListData,
} from '../types/priceList';
import priceListService from '../services/priceListService';

export const usePriceListsPage = (filters: PriceListFilters) => {
  return useQuery({
    queryKey: ['priceLists', 'page', filters],
    queryFn: () => priceListService.getAllPriceLists(filters),
    placeholderData: keepPreviousData,
  });
};

export const usePriceListSummary = () => {
  return useQuery({
    queryKey: ['priceLists', 'summary'],
    queryFn: () => priceListService.getSummary(),
  });
};

const useInvalidatePriceLists = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['priceLists'] });
};

export const useCreatePriceList = () => {
  const invalidate = useInvalidatePriceLists();
  return useMutation({
    mutationFn: (data: CreatePriceListData) => priceListService.createPriceList(data),
    onSuccess: invalidate,
  });
};

export const useUpdatePriceList = () => {
  const invalidate = useInvalidatePriceLists();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePriceListData }) =>
      priceListService.updatePriceList(id, data),
    onSuccess: invalidate,
  });
};

export const useDeletePriceList = () => {
  const invalidate = useInvalidatePriceLists();
  return useMutation({
    mutationFn: (id: string) => priceListService.deletePriceList(id),
    onSuccess: invalidate,
  });
};

export const useSetPriceListActive = () => {
  const invalidate = useInvalidatePriceLists();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? priceListService.activatePriceList(id) : priceListService.deactivatePriceList(id),
    onSuccess: invalidate,
  });
};
